// Payment plan for an accepted estimate (KAN-1118 / RD-BILL-039): bill it in 1, 2 or 3 invoices.
// PURE — no React, no supabase. Everything the Bill-estimate flow, the estimate page's Payments
// card and the estimate row's progress line need comes from here, so no screen re-derives it.
//
// Stored shape (`estimates.payment_plan`, written only by `set_estimate_payment_plan`):
//   { count: 1|2|3, percents: [p1, …] }   — count-1 entries; the LAST payment is always "the rest".
// NULL = legacy / no plan: derived from the live linked invoices.
//
// Money: every amount is integer cents inside, dollars out. A non-last slot is
// convertPortionTotal(factor = p/100) — the SAME formula the invoice snapshot uses — and the last slot is
// the exact remainder (estimate total − every other slot), so the plan always sums to the total to the cent.
import { convertPortionTotal } from './convertEstimateInvoiceSnapshot';
import { invoicePreCreditTotal } from '../invoice/invoicePreCreditTotal';
import { parseDbInstant, toTenantDateStr, tenantToday } from '../date/tenantDate';

export const MAX_PAYMENT_PLAN_COUNT = 3;
const OVERDUE_FALLBACK_DAYS = 30;
const EPS = 0.0001;

const cents = (n) => Math.round((Number(n) || 0) * 100);
const fromCents = (c) => c / 100;
export const round2 = (n) => Math.round(Number(n) * 100) / 100;
const isVoided = (inv) => String(inv?.status || '').toLowerCase() === 'voided' || Boolean(inv?.deleted_at);

/** Preset percents of the NON-last payments: 1 → [], 2 → [50], 3 → [30, 40]. */
export function planPresets(n) {
  if (n === 2) return [50];
  if (n === 3) return [30, 40];
  return [];
}

/** "33.33", "40", "66.67" — up to 2 decimals, trailing zeros trimmed. */
export function formatPlanPercent(value, locale = 'en') {
  const n = round2(value);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(n);
}

/** i18n id of a slot's name; the middle slot takes `{ n }` (always safe to pass). */
export function slotLabelKey(seq, count) {
  if (count <= 1) return 'estimate.paymentPlan.slot.full';
  if (seq <= 1) return 'estimate.paymentPlan.slot.deposit';
  if (seq >= count) return 'estimate.paymentPlan.slot.final';
  return 'estimate.paymentPlan.slot.payment';
}

/**
 * Check the typed percents of the non-last payments.
 * @returns {{ ok: boolean, code: null|'invalid'|'sum', sum: number }} 'sum' = they add up to 100 or more.
 */
export function validatePlanPercents(count, percents) {
  const need = Math.max(0, count - 1);
  const list = (percents || []).slice(0, need);
  if (list.length < need) return { ok: false, code: 'invalid', sum: 0 };
  let sum = 0;
  for (const p of list) {
    const n = Number(p);
    if (!Number.isFinite(n) || n <= 0 || Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) {
      return { ok: false, code: 'invalid', sum: 0 };
    }
    sum += n;
  }
  sum = round2(sum);
  if (need > 0 && sum >= 100 - EPS) return { ok: false, code: 'sum', sum };
  return { ok: true, code: null, sum };
}

function validStored(plan) {
  if (!plan || typeof plan !== 'object') return null;
  const count = Number(plan.count);
  if (!Number.isInteger(count) || count < 1 || count > MAX_PAYMENT_PLAN_COUNT) return null;
  const percents = Array.isArray(plan.percents) ? plan.percents.map(Number) : [];
  if (percents.length !== count - 1 || !validatePlanPercents(count, percents).ok) return null;
  return { count, percents };
}

function addDaysYmd(ymd, days) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

/** Mirrors isInvoiceOverdue (billingPipeline) and the server's earliest_unpaid_due_on: 'sent' only, due_date else sent/created + 30. */
function isOverdue(inv, tz, today) {
  if (String(inv?.status || '').toLowerCase() !== 'sent') return false;
  let due = inv.due_date ? String(inv.due_date).slice(0, 10) : '';
  if (!due) {
    const raw = inv.sent_at || inv.created_at;
    if (!raw) return false;
    const base = toTenantDateStr(parseDbInstant(raw), tz);
    if (!base) return false;
    due = addDaysYmd(base, OVERDUE_FALLBACK_DAYS);
  }
  return today > due;
}

function invoiceState(inv, tz, today) {
  const status = String(inv?.status || '').toLowerCase();
  if (status === 'paid') return 'paid';
  if (status === 'draft') return 'draft';
  return isOverdue(inv, tz, today) ? 'overdue' : 'sent';
}

/** Put each live invoice in its slot: by estimate_invoice_sequence, unsequenced legacy rows into the lowest free slots. */
function assignInvoices(live) {
  const bySlot = new Map();
  const loose = [];
  const byAge = [...live].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  byAge.forEach((inv) => {
    const seq = inv.estimate_invoice_sequence;
    if (typeof seq === 'number' && seq >= 1 && seq <= MAX_PAYMENT_PLAN_COUNT && !bySlot.has(seq)) bySlot.set(seq, inv);
    else loose.push(inv);
  });
  loose.forEach((inv) => {
    for (let s = 1; s <= MAX_PAYMENT_PLAN_COUNT; s += 1) {
      if (!bySlot.has(s)) { bySlot.set(s, inv); break; }
    }
  });
  return bySlot;
}

/**
 * THE count rule of a plan with no stored payment_plan (legacy / NULL), shared by normalizePlan (the page) and
 * estimateProgressModel (the rows) so they can never disagree: the live invoices (or the highest live sequence),
 * plus one more slot when what is billed does not yet cover the total; never above 3.
 */
export function derivedPlanCount({ liveCount, maxSeq = 0, billedC, totalC }) {
  let count = Math.max(liveCount, maxSeq, 1);
  if (totalC > cents(EPS) && billedC < totalC - 2 && count === liveCount && count < MAX_PAYMENT_PLAN_COUNT) count += 1;
  return count;
}

/**
 * The plan to show for an estimate: a stored plan, or one derived from its invoices (legacy / NULL plan).
 * A derived plan has count = max(live invoices, 1), plus one more slot when what is billed does not yet
 * cover the total (a legacy part-billed estimate still owes its remainder); it never exceeds 3.
 *
 * @param {object} estimate - { total, subtotal?, discount?, tax_amount?, payment_plan? }
 * @param {Array} invoices - every invoice linked to the estimate (voided ones only mark a slot 'voided_rebill').
 * @param {{ tz?: string, today?: string }} [opts] - tenant tz and tenant-local today ('YYYY-MM-DD') for 'overdue'.
 * @returns {{ count: number, percents: number[], derived: boolean, slots: object[], billedCount: number,
 *   billedAmount: number, remaining: number, nextSeq: number|null }}
 *   slot = { seq, labelKey, percent, isRest, amount, invoice, state, factor }
 *   state = 'paid'|'sent'|'overdue'|'draft'|'voided_rebill'|'unbilled'
 */
export function normalizePlan(estimate, invoices, opts = {}) {
  const tz = opts.tz || 'UTC';
  const today = opts.today || tenantToday(tz);
  const total = Number(estimate?.total) || 0;
  const all = invoices || [];
  const live = all.filter((i) => !isVoided(i));
  const bySlot = assignInvoices(live);
  const voidedSeqs = new Set(all.filter(isVoided).map((i) => i.estimate_invoice_sequence).filter((s) => typeof s === 'number'));
  const maxBilledSeq = Math.max(0, ...bySlot.keys());
  const billedC = live.reduce((s, i) => s + cents(invoicePreCreditTotal(i)), 0);

  let stored = validStored(estimate?.payment_plan);
  if (stored && (stored.count < maxBilledSeq || stored.count < live.length)) stored = null;

  let count;
  let percents;
  if (stored) {
    ({ count, percents } = stored);
  } else {
    count = derivedPlanCount({ liveCount: live.length, maxSeq: maxBilledSeq, billedC, totalC: cents(total) });
    percents = derivePercents(count, total, bySlot);
  }

  // Slot amounts in cents: billed = the invoice itself; unbilled non-last = convertPortionTotal; last = the rest.
  const subtotal = Number(estimate?.subtotal) > 0 ? Number(estimate.subtotal) : total;
  const useParts = Number(estimate?.subtotal) > 0;
  const planned = (p) => cents(convertPortionTotal({
    subtotal, discount: useParts ? estimate.discount : 0, taxAmount: useParts ? estimate.tax_amount : 0, factor: p / 100,
  }));
  const amountC = [];
  for (let k = 1; k < count; k += 1) {
    const inv = bySlot.get(k);
    amountC.push(inv ? cents(invoicePreCreditTotal(inv)) : planned(percents[k - 1]));
  }
  const lastInv = bySlot.get(count);
  const restC = Math.max(0, cents(total) - amountC.reduce((s, c) => s + c, 0));
  amountC.push(lastInv ? cents(invoicePreCreditTotal(lastInv)) : restC);

  const restPercent = round2(100 - percents.reduce((s, p) => s + p, 0));
  const slots = amountC.map((c, i) => {
    const seq = i + 1;
    const isRest = seq === count;
    const invoice = bySlot.get(seq) || null;
    const percent = isRest ? (count === 1 ? 100 : restPercent) : percents[i];
    return {
      seq,
      labelKey: slotLabelKey(seq, count),
      percent,
      isRest,
      amount: fromCents(c),
      invoice,
      state: invoice ? invoiceState(invoice, tz, today) : (voidedSeqs.has(seq) ? 'voided_rebill' : 'unbilled'),
      factor: isRest ? (total > EPS ? fromCents(c) / total : 1) : percent / 100,
    };
  });
  const next = slots.find((s) => !s.invoice);
  return {
    count,
    percents,
    derived: !stored,
    slots,
    billedCount: live.length,
    billedAmount: fromCents(billedC),
    remaining: Math.max(0, fromCents(cents(total) - billedC)),
    nextSeq: next ? next.seq : null,
  };
}

/** Percents of the non-last slots for a plan derived from invoices (see normalizePlan). */
function derivePercents(count, total, bySlot) {
  if (count <= 1) return [];
  const out = [];
  let usedPct = 0;
  const unbilled = [];
  for (let k = 1; k < count; k += 1) {
    const inv = bySlot.get(k);
    if (inv && total > EPS) {
      const pct = round2((invoicePreCreditTotal(inv) / total) * 100);
      out.push(pct);
      usedPct += pct;
    } else {
      out.push(null);
      unbilled.push(k - 1);
    }
  }
  if (unbilled.length) {
    const lastInv = bySlot.get(count);
    const lastPct = lastInv && total > EPS ? round2((invoicePreCreditTotal(lastInv) / total) * 100) : 0;
    const left = Math.max(0.01, round2(100 - usedPct - lastPct));
    const share = lastInv ? round2(left / unbilled.length) : round2(Math.min(left, 50) / unbilled.length);
    unbilled.forEach((i) => { out[i] = Math.max(0.01, share); });
  }
  return out;
}

/**
 * The fewest payments the Bill-estimate flow may offer (client mirror of the server floor, KAN-1118):
 * never below the live invoice count or the highest live sequence, and while billed money does not cover the
 * total one slot must stay unbilled — so "1 payment" is only offered when billed ≈ total (or nothing is billed).
 * @param {{ slots: Array, billedCount: number, billedAmount: number }} plan - normalizePlan result.
 */
export function minPlanCount(plan, total) {
  const maxSeq = Math.max(0, ...(plan?.slots || []).filter((s) => s.invoice).map((s) => s.seq));
  const live = Number(plan?.billedCount) || 0;
  const covers = !plan || live === 0 || (Number(plan.billedAmount) || 0) >= (Number(total) || 0) - 0.02;
  return Math.max(1, live, maxSeq + (covers ? 0 : 1));
}

/** The next slot to bill: the lowest slot with no live invoice, or null when every slot is billed. */
export function nextBillableSlot(plan) {
  return plan?.slots?.find((s) => !s.invoice) || null;
}

/**
 * Model of the estimate row's progress line, read from the server-derived progress columns
 * (billed_invoice_count, billed_amount, paid_amount, earliest_unpaid_due_on/sequence, fully_paid_at).
 * Strings are composed by the caller (i18n lives there):
 *   kind 'none'    → "Nothing billed yet · {toBill} to bill"
 *   kind 'billing' → "{billedCount} of {count} billed · {paidAmount} paid"
 *   kind 'collect' → the same + " · {toCollect} to collect" (fully billed, not all paid)
 *   kind 'paid'    → "Paid in full · {paidInFullAt}"
 * `overdue` adds the amber " · Payment {overdueSequence} overdue".
 */
export function estimateProgressModel(estimate, tz = 'UTC', today = null) {
  const now = today || tenantToday(tz);
  const total = Number(estimate?.total) || 0;
  const billedCount = Number(estimate?.billed_invoice_count) || 0;
  const billedAmount = Number(estimate?.billed_amount) || 0;
  const paidAmount = Number(estimate?.paid_amount) || 0;
  const stored = validStored(estimate?.payment_plan);
  const count = stored ? stored.count : derivedPlanCount({ liveCount: billedCount, billedC: cents(billedAmount), totalC: cents(total) });
  const fullyBilled = billedCount >= count && (total <= EPS || cents(billedAmount) >= cents(total) - 2);
  const dueOn = estimate?.earliest_unpaid_due_on ? String(estimate.earliest_unpaid_due_on).slice(0, 10) : null;
  const overdue = Boolean(dueOn) && now > dueOn;
  let kind = 'billing';
  if (estimate?.fully_paid_at) kind = 'paid';
  else if (billedCount === 0) kind = 'none';
  else if (fullyBilled && cents(paidAmount) < cents(billedAmount)) kind = 'collect';
  return {
    kind,
    count,
    billedCount,
    total,
    billedAmount,
    paidAmount,
    toBill: Math.max(0, round2(total - billedAmount)),
    toCollect: Math.max(0, round2(billedAmount - paidAmount)),
    paidInFullAt: estimate?.fully_paid_at || null,
    overdue,
    overdueSequence: overdue ? (Number(estimate?.earliest_unpaid_due_sequence) || null) : null,
  };
}
